import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Link } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, DropResult, DroppableProvided } from 'react-beautiful-dnd';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import DisplayDate from '../../components/DisplayDate';
import Button from '../../components/Button';
import StatusIcon from '../../components/StatusIcon';
import DisplayDragIcon from '../../components/DisplayDragIcon';
import LoadingSpinner from '../../components/LoadingSpinner';
import GlobalModal from '../../components/GlobalModal';
import RemoveModal from './RemoveModal';

// ICONS
import { ReactComponent as PlusIcon } from '../../assets/svg/plus.svg';

// TYPES
import { ComplaintAdviceDashboardComplaintAdvice, ComplaintAdviceReorderCommand } from '../../api/models';

const ContentComplaintsAdvice: React.FC = () => {
	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Get Website Url from Redux store
	const websiteUrl = useAppSelector((state) => state.lists.websiteUrl);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// State to control Modal
	const [showRemoveModal, setShowRemoveModal] = React.useState<boolean>(false);

	// State to store Advice Id
	const [adviceId, setAdviceId] = React.useState<string>('');

	// State to store Child Safe Advices title
	const [adviceTitle, setAdviceTitle] = React.useState<string>('');

	// Set up Data state
	const [complaintsAdviceData, setComplaintsAdviceData] = React.useState<ComplaintAdviceDashboardComplaintAdvice[]>([]);

	const getComplaintsAdviceData = async () => {
		setIsLoading(true);

		const response = await apibridge.getComplaintAdviceDashboard();

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = [] } = response;

			setIsLoading(false);

			setComplaintsAdviceData(result);
		}
	};

	React.useEffect(() => {
		getComplaintsAdviceData();
	}, []);

	// Handling when we drag
	const onDragEndHandler = (result: DropResult) => {
		if (!result.destination) return;

		const items = Array.from(complaintsAdviceData);

		const [reorderedItem] = items.splice(result.source.index, 1);

		items.splice(result.destination.index, 0, reorderedItem);

		setComplaintsAdviceData(items);

		const data: ComplaintAdviceReorderCommand = { id: reorderedItem.id, order: result.destination.index + 1 };

		apibridge.postComplaintAdviceReorder(data);
	};

	const removeHandler = async (item: ComplaintAdviceDashboardComplaintAdvice) => {
		setAdviceId(item.id || '');

		setAdviceTitle(item.title || '');

		setShowRemoveModal(true);
	};

	return (
		<div className="page-general-layout">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<div className="single-btn-right-container border">
									<Link className="btn-primary btn-right" to="/content-complaints-advice-edit">
										<PlusIcon className="icon-link" /> Add new
									</Link>
								</div>
								<section className="info-section mtop-0">
									{complaintsAdviceData && complaintsAdviceData.length > 0 ? (
										<DragDropContext onDragEnd={onDragEndHandler}>
											<Droppable droppableId="trainingModulesItem">
												{(provided: DroppableProvided) => (
													<ul {...provided.droppableProps} ref={provided.innerRef}>
														{complaintsAdviceData.map((item, index) => {
															return (
																<Draggable
																	key={index}
																	draggableId={index.toString()}
																	index={index}
																	isDragDisabled={isOrganisationActive === false}
																>
																	{(provided) => (
																		<li
																			className="mbtom-16 "
																			ref={provided.innerRef}
																			{...provided.draggableProps}
																			{...provided.dragHandleProps}
																		>
																			<div className="row detail-row">
																				<div className="col-auto pdl-0 pdr-0">
																					<StatusIcon statusType="read" />
																				</div>

																				<div className="col-md-auto full-width">
																					<div className="row detail-info">
																						<div className="col-10 col-md-6 detail-name">{item.title}</div>

																						<div className="col-10 col-md-6 text-category-detail">
																							Last updated &nbsp; &nbsp;
																							<span className="text-subtitle">
																								<DisplayDate>{item.updated}</DisplayDate>
																							</span>
																						</div>
																					</div>

																					<div className="d-block d-sm-none">
																						<a className="btn-text remove link" download href={item.attachmentUrl}>
																							Download
																						</a>

																						<Link
																							className="btn-text remove link"
																							to={`/content-complaints-advice-edit/${item.id}`}
																						>
																							Edit
																						</Link>

																						<Button className="btn-text link pdr-0" onClick={() => removeHandler(item)}>
																							Remove
																						</Button>
																					</div>
																				</div>

																				<div className="col-auto pdr-0 link-group content-training-module-page">
																					<div className="d-none d-sm-flex">
																						<a className="btn-text remove link" download href={item.attachmentUrl}>
																							Download
																						</a>

																						<Link
																							className="btn-text remove link"
																							to={`/content-complaints-advice-edit/${item.id}`}
																						>
																							Edit
																						</Link>

																						<Button className="btn-text link pdr-0" onClick={() => removeHandler(item)}>
																							Remove
																						</Button>
																					</div>

																					<div className="drag-icon-container">
																						<DisplayDragIcon index={index} array={complaintsAdviceData} />
																					</div>
																				</div>
																			</div>
																		</li>
																	)}
																</Draggable>
															);
														})}
														{provided.placeholder}
													</ul>
												)}
											</Droppable>
										</DragDropContext>
									) : (
										<div className="detail-prompt">
											Information in this section is coming soon. In the meantime feel free to{' '}
											<a href={`${websiteUrl}/contact-us`} target="_blank">
												contact us
											</a>
											.
										</div>
									)}
								</section>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}

			<GlobalModal isOpen={showRemoveModal} setIsOpen={setShowRemoveModal} titleText="Remove advice">
				<RemoveModal
					complaintsAdviceData={complaintsAdviceData}
					setComplaintsAdviceData={setComplaintsAdviceData}
					getComplaintsAdviceData={getComplaintsAdviceData}
					adviceId={adviceId}
					adviceTitle={adviceTitle}
				/>
			</GlobalModal>
		</div>
	);
};

export default ContentComplaintsAdvice;
